import { TablePaginationOwnProps } from '@mui/material';
import {
  ColumnOrderState,
  getCoreRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  Table,
  TableOptions,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useEffect } from 'react';

type PaginationProps = {
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState> | undefined;
  paginationCount: TablePaginationOwnProps['count'];
  onPageChange: TablePaginationOwnProps['onPageChange'];
  onRowsPerPageChange: TablePaginationOwnProps['onRowsPerPageChange'];
  rowsPerPageOptions: TablePaginationOwnProps['rowsPerPageOptions'];
  showFirstButton?: TablePaginationOwnProps['showFirstButton'];
  showLastButton?: TablePaginationOwnProps['showLastButton'];
};

type ColumnVisibilityProps = {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
};

type ColumnOrderProps = {
  columnOrder: ColumnOrderState;
  onColumnOrderChange: OnChangeFn<ColumnOrderState>;
};

type RowSelectionProps = {
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
};

type BaseTableOptions<TData> = {
  tableData: TableOptions<TData>['data'];
  columns: TableOptions<TData>['columns'];
  getRowId?: ((originalRow: TData, index: number, parent?: Row<TData> | undefined) => string) | undefined;
};

type PaginationFeature = ({ hasPagination: true } & PaginationProps) | { hasPagination: false };

type ColumnVisibilityFeature = ({ isColumnVisibilityEnabled: true } & ColumnVisibilityProps) | { isColumnVisibilityEnabled: false };

type ColumnOrderFeature = ({ isColumnOrderEnabled: true } & ColumnOrderProps) | { isColumnOrderEnabled: false };

type RowSelectionFeature = ({ isRowSelectionEnabled: true } & RowSelectionProps) | { isRowSelectionEnabled: false };

type UseBaseTableInput<TData> = BaseTableOptions<TData> &
  PaginationFeature &
  ColumnVisibilityFeature &
  ColumnOrderFeature &
  RowSelectionFeature;

export type BaseTableConfig<TData> = UseBaseTableInput<TData> & {
  table: Table<TData>;
};

function useBaseTable<T>(props: UseBaseTableInput<T>): BaseTableConfig<T> {
  const pageCount = props.hasPagination ? Math.ceil(props.paginationCount / props.pagination.pageSize) : undefined;

  const table = useReactTable<T>({
    data: props.tableData,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination: props.hasPagination ? props.pagination : undefined,
      columnVisibility: props.isColumnVisibilityEnabled ? props.columnVisibility : undefined,
      columnOrder: props.isColumnOrderEnabled ? props.columnOrder : undefined,
      rowSelection: props.isRowSelectionEnabled ? props.rowSelection : undefined,
    },
    columns: props.columns,
    pageCount,
    manualPagination: true,
    autoResetPageIndex: false,
    onPaginationChange: props.hasPagination ? props.onPaginationChange : undefined,
    onColumnVisibilityChange: props.isColumnVisibilityEnabled ? props.onColumnVisibilityChange : undefined,
    onColumnOrderChange: props.isColumnOrderEnabled ? props.onColumnOrderChange : undefined,
    enableRowSelection: props.isRowSelectionEnabled,
    onRowSelectionChange: props.isRowSelectionEnabled ? props.onRowSelectionChange : undefined,
    getRowId: props.getRowId,
  });

  useEffect(() => {
    if (props.isColumnOrderEnabled && table.getState().columnOrder.length === 0) {
      table.setColumnOrder(table.getAllLeafColumns().map((col) => col.id));
    }
  }, [props.isColumnOrderEnabled, table]);

  return { ...props, table };
}

export default useBaseTable;
